// Bag tag page. Opens from a bag tag: getsafetag.com/?tag=01-QEW14.
// Words: TAG_CLAIM_WORDS.md. Mockup: MOCKUP_TAG_CLAIM_APPROVED.html (2026-10-06).
// An unclaimed tag opens the live seven-step claim, ported as designed, in the
// program colors. Only the LOTO step changed: the date is sent for verification.
// A claimed tag shows its holder until QEW sign-in and the Dashboard land.

import { steps } from "./step-back";
import "./claim.css";
import { openScanAgain } from "./scan-again-view";
import {
  DEPARTMENTS,
  EXPERIENCE,
  ROLES,
  lotoCheck,
  lotoLine,
  sessionLine,
  shortDate,
  type SessionCheck,
  type TagView,
} from "./claim";
import { callServer } from "./firebase";
import { NODES } from "./training";
import { dateInSiteTime } from "./training-server";

type ClaimResult =
  | { kind: "claimed"; tagId: string; holderName: string; siteName: string; lotoReported: boolean }
  | { kind: "alreadyClaimed"; tagId: string; holderName: string }
  | { kind: "session"; message: string };

const LAST_STEP = 6;
const STASH_KEY = "safetag_claim_";

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// ---- Pieces shared by every tag screen (live tagHeaderHtml, tagStripHtml) ----

const SCAN_ICON = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
  <rect x="3" y="3" width="5" height="5"/><rect x="16" y="3" width="5" height="5"/>
  <rect x="3" y="16" width="5" height="5"/><line x1="21" y1="16" x2="21" y2="21"/>
  <line x1="16" y1="21" x2="21" y2="21"/><line x1="16" y1="16" x2="16" y2="16"/></svg>`;

export function headerHtml(): string {
  return `<div class="header"><div>
      <div class="header-eyebrow">Alltech — ESP Field Access</div>
      <div class="header-title">QEW Tag</div>
    </div>
    <button class="header-scan-again" data-act="scan">${SCAN_ICON}Scan Again</button></div>`;
}

/** Tag strip. Site by name only (approved 2026-10-06). */
export function stripHtml(tagId: string, siteName: string, state: string, cls = ""): string {
  return `<div class="tagstrip"><div>
      <div class="tagstrip-lbl">QEW Tag</div>
      <div class="tagstrip-id">${esc(tagId)}</div>
      <div class="tagstrip-site">${esc(siteName)}</div>
    </div><div class="tagstrip-state ${cls}">${state}</div></div>`;
}

// ---- Step icons, live set, drawn in gold on the black tile ----

const ICONS: Record<string, string> = {
  sess: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>',
  name: '<circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>',
  dept: '<rect x="5" y="3" width="14" height="18" rx="1"/><rect x="8" y="7" width="2.2" height="2.2"/><rect x="13.8" y="7" width="2.2" height="2.2"/><rect x="8" y="11.4" width="2.2" height="2.2"/><rect x="13.8" y="11.4" width="2.2" height="2.2"/><rect x="9.7" y="16.2" width="4.6" height="4.8"/>',
  role: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0114 0"/>',
  years: '<path d="M13 2L4.5 13h6l-1 9L19.5 11h-6L13 2z" fill="#F5C400" stroke="none"/>',
  loto: '<rect x="5" y="11" width="14" height="9" rx="1.5"/><path d="M8 11V8a4 4 0 018 0v3"/><circle cx="12" cy="15" r="1.4" fill="#F5C400" stroke="none"/>',
  review: '<path d="M5 13l4 4L19 7"/>',
};

function stepHead(n: number, icon: string, title: string, help: string): string {
  return `<div class="step-count"><span class="step-count-dot"></span>Step ${n + 1} of ${LAST_STEP + 1}</div>
    <div class="step-icon"><svg viewBox="0 0 24 24" fill="none" stroke="#F5C400" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[icon]}</svg></div>
    <div class="step-q">${title}</div>
    <div class="step-help">${help}</div>`;
}

function choices(field: string, values: readonly string[]): string {
  return `<div class="choices">${values
    .map((v) => `<button class="choice" data-field="${field}" data-val="${esc(v)}">${esc(v)}<span class="choice-tick">✓</span></button>`)
    .join("")}</div>`;
}

function reviewRow(label: string, id: string, step: number, pill = false): string {
  return `<div class="review-row"><div class="review-lbl">${label}</div><div class="review-right">
      ${pill ? `<span class="review-pill" id="${id}Pill" style="display:none;"></span>` : ""}
      <div class="review-val" id="${id}">—</div>
      <button class="review-edit" data-step="${step}">Edit</button></div></div>`;
}

/** The seven steps, live markup. LOTO help line replaced (approved 2026-10-06). */
export function enrollmentHtml(tagId: string, siteName: string): string {
  const rail = Array.from({ length: LAST_STEP + 1 }, (_, i) => `<div class="rail-seg${i === 0 ? " active" : ""}" id="tagR${i}"></div>`).join("");
  return `<div class="phone">${headerHtml()}
    <div id="tagArea">${stripHtml(tagId, siteName, "Unclaimed")}<div class="rail">${rail}</div></div>
    <div class="stepwrap" id="tagStepwrap">
      <div class="step active" id="tagSt0">
        ${stepHead(0, "sess", 'Training Session Code <span style="font-size:13px;font-weight:700;color:#999;letter-spacing:0;">(Auto Generated)</span>', "Session code unlocks card.")}
        <input class="big-input" id="tagSess" type="text" placeholder="Session code" autocomplete="off" spellcheck="false" autocapitalize="characters" maxlength="4">
        <div class="note note-bad" id="tagSessBad"></div><div class="note note-ok" id="tagSessGood"></div><div class="note note-work" id="tagSessWork"></div>
      </div>
      <div class="step" id="tagSt1">
        ${stepHead(1, "name", "Name", "")}
        <input class="big-input input-gap" id="tagFirst" type="text" placeholder="First name" autocomplete="given-name">
        <input class="big-input" id="tagLast" type="text" placeholder="Last name" autocomplete="family-name">
      </div>
      <div class="step" id="tagSt2">${stepHead(2, "dept", "Department", "Where you report, not where you're working today.")}${choices("dept", DEPARTMENTS)}</div>
      <div class="step" id="tagSt3">${stepHead(3, "role", "Role", "Your job title — separate from the QEW level you'll be assigned after training.")}${choices("role", ROLES)}</div>
      <div class="step" id="tagSt4">${stepHead(4, "years", "Electrical Experience", "Total across your career, not just at this site. A best estimate is fine.")}${choices("years", EXPERIENCE)}</div>
      <div class="step" id="tagSt5">
        ${stepHead(5, "loto", "LOTO Training Date", "Bring your LOTO authorization date. Your manager or Safety confirms it. A verified LOTO date is required to be a Qualified Electrical Worker.")}
        <input class="big-input" id="tagLoto" type="date">
        <div class="loto-confirm" id="tagLotoOk"><span class="loto-confirm-icon" id="tagLotoIcon">✓</span><span class="loto-confirm-text" id="tagLotoOkText"></span></div>
        <div class="loto-flag" id="tagLotoFlag"><span class="loto-flag-icon">✕</span><span class="loto-flag-text" id="tagLotoFlagText"></span></div>
        <div class="note note-bad" id="tagLotoFutureErr"></div>
        <label class="loto-skip-row" for="tagLotoSkip"><input type="checkbox" id="tagLotoSkip"><span class="loto-skip-lbl">I don’t currently have a LOTO authorization</span></label>
      </div>
      <div class="step" id="tagSt6">
        ${stepHead(6, "review", "Review", "When scanned, this tag shows your glove and training records.")}
        <div class="review-card">
          ${reviewRow("Session", "tagRvSess", 0)}${reviewRow("Name", "tagRvName", 1)}${reviewRow("Department", "tagRvDept", 2)}
          ${reviewRow("Role", "tagRvRole", 3)}${reviewRow("Experience", "tagRvYears", 4)}${reviewRow("LOTO Training", "tagRvLoto", 5, true)}
          <div class="review-row"><div class="review-lbl">Level</div><div class="review-right"><div class="review-val" style="color:#999;">Trainee</div></div></div>
        </div>
        <div class="note note-bad" id="tagClaimErr" style="margin-top:14px;"></div>
      </div>
    </div>
    <div class="navbar" id="tagNav">
      <button class="btn-back" id="tagBackBtn" style="display:none;">Back</button>
      <button class="btn-next" id="tagNextBtn" disabled>Continue</button>
    </div></div>`;
}

/** After Claim. Live confirmation, with the 4-node training chain. */
export function confirmationHtml(r: Extract<ClaimResult, { kind: "claimed" }>): string {
  const lotoNote = r.lotoReported
    ? `<div class="loto-wait-card"><div class="loto-wait-hdr"><span style="font-size:15px;">⏳</span><div class="loto-wait-title">LOTO date sent for verification</div></div>
        <div class="loto-wait-msg">Your manager, site manager or Safety confirms it. You are a Qualified Electrical Worker once it is confirmed and training is complete.</div></div>`
    : `<div class="loto-wait-card"><div class="loto-wait-hdr"><span style="font-size:15px;">⏳</span><div class="loto-wait-title">LOTO authorization needed</div></div>
        <div class="loto-wait-msg">No LOTO date on file yet. QEW status waits on a verified LOTO date. Report it on this tag when you have it.</div></div>`;
  const chain = NODES.map((n, i) => {
    const next = i === 0;
    const cls = next ? "node-next" : "node-pending";
    return `<div class="node-row"><div class="node-rail"><div class="node-dot ${cls}">${i + 1}</div>${i < NODES.length - 1 ? '<div class="node-line"></div>' : ""}</div>
      <div class="node-label ${cls}">Node ${i + 1} · ${esc(n.nodeName)}${next ? '<div class="node-next-tag">Next up</div>' : ""}</div></div>`;
  }).join("");
  return `<div class="phone">${headerHtml()}
    <div class="done-hdr"><div class="done-check">✓</div><div class="done-title">Tag claimed</div><div class="done-sub">Record Saved</div></div>
    <div class="name-banner"><div class="name-banner-eyebrow">This tag now belongs to</div>
      <div class="name-banner-name">${esc(r.holderName)}</div><div class="name-banner-tag">${esc(r.tagId)} · ${esc(r.siteName)}</div></div>
    ${lotoNote}
    <div class="prog-card"><div class="prog-top"><div class="prog-lbl">Training progress</div><div class="prog-count">0 <span>of ${NODES.length}</span></div></div>
      <div class="node-chain">${chain}</div></div>
    <div class="locked-card"><div class="locked-hdr"><span style="font-size:15px;">🔒</span><div class="locked-title">PPE requirements</div></div>
      <div class="locked-msg">Glove records unlock when training Nodes 1 to 3 are complete. Scan this tag any time to check where you stand.</div></div>
    <div class="bottom-pad"></div></div>`;
}

/** Someone else's claim landed first. Live screen, with the way out kept. */
export function alreadyClaimedHtml(tagId: string, holderName: string): string {
  return `<div class="phone">${headerHtml()}
    <div class="done-hdr yellow"><div class="done-check">!</div><div class="done-title">This tag was just claimed</div><div class="done-sub">See your ESP Manager to sort this out</div></div>
    <div class="name-banner"><div class="name-banner-eyebrow">Tag now belongs to</div><div class="name-banner-name">${esc(holderName || "—")}</div><div class="name-banner-tag">${esc(tagId)}</div></div>
    <div class="msg-card"><div class="msg-title">Your entries were saved</div>
      <div class="msg-body">Your name, department, role, and years of experience are held on this device. Show this screen to your ESP Manager. They can move your record to a fresh tag.</div></div>
    <div class="navbar"><button class="btn-next" data-act="reload">View This Tag’s Status</button></div>
    <div class="bottom-pad"></div></div>`;
}

/** A claimed tag, until QEW sign-in and the Dashboard land (Stage 2 items 3 and 4). */
export function claimedHtml(v: Extract<TagView, { kind: "claimed" }>): string {
  return `<div class="phone">${headerHtml()}${stripHtml(v.tagId, v.siteName, "Claimed", "claimed")}
    <div class="name-banner"><div class="name-banner-eyebrow">This tag belongs to</div><div class="name-banner-name">${esc(v.holderName || "—")}</div>
      <div class="name-banner-tag">${esc(v.tagId)} · ${esc(v.siteName)}</div></div>
    <div class="msg-card"><div class="msg-title">Sign-in comes next</div>
      <div class="msg-body">Your QEW Dashboard opens from this tag once QEW sign-in is on the new app. Use the current app until then.</div></div>
    <div class="bottom-pad"></div></div>`;
}

function statusHtml(title: string, sub: string, cls: string, cardTitle: string, body: string, strip = ""): string {
  return `<div class="phone">${headerHtml()}${strip}
    <div class="done-hdr ${cls}"${strip ? ' style="margin-top:12px;"' : ""}><div class="done-check">${cls === "red" ? "✕" : "⚠"}</div><div class="done-title">${title}</div><div class="done-sub">${sub}</div></div>
    <div class="msg-card"><div class="msg-title">${cardTitle}</div><div class="msg-body">${body}</div></div>
    <div class="bottom-pad"></div></div>`;
}

function loadingHtml(): string {
  return `<div class="phone">${headerHtml()}<div class="loading-wrap"><div class="spinner"></div><div class="loading-msg">Loading tag…</div></div></div>`;
}

function errorHtml(msg: string): string {
  return `<div class="phone">${headerHtml()}
    <div class="done-hdr red"><div class="done-check">⚠</div><div class="done-title">Something went wrong</div><div class="done-sub">See details below</div></div>
    <div class="msg-card"><div class="msg-body">${esc(msg)}</div></div>
    <div class="navbar"><button class="btn-next" data-act="reload">Try again</button></div></div>`;
}

// ---- The page ----

interface Draft {
  sess: string;
  first: string;
  last: string;
  dept: string;
  role: string;
  years: string;
  lotoDate: string;
  lotoSkipped: boolean;
}

function emptyDraft(): Draft {
  return { sess: "", first: "", last: "", dept: "", role: "", years: "", lotoDate: "", lotoSkipped: false };
}

function stash(tagId: string, d: Draft): void {
  try {
    sessionStorage.setItem(STASH_KEY + tagId, JSON.stringify(d));
  } catch {
    /* private mode or full storage: the claim still goes */
  }
}
function recover(tagId: string): Draft | null {
  try {
    const raw = sessionStorage.getItem(STASH_KEY + tagId);
    return raw ? { ...emptyDraft(), ...(JSON.parse(raw) as Partial<Draft>) } : null;
  } catch {
    return null;
  }
}
function clearStash(tagId: string): void {
  try {
    sessionStorage.removeItem(STASH_KEY + tagId);
  } catch {
    /* nothing to clear */
  }
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

/**
 * onClaimed: a claimed tag hands over to QEW Field Access ("Is this you?").
 * Without it the tag shows its holder, as before sign-in landed.
 */
export function mountTagPage(root: HTMLElement, tagId: string, company: string, onClaimed?: (v: Extract<TagView, { kind: "claimed" }>) => void): void {
  document.body.classList.add("tagpage");
  root.innerHTML = `<div id="tagRoot"></div>`;
  const box = root.querySelector<HTMLElement>("#tagRoot")!;
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => box.querySelector<T>(`#${id}`)!;
  let view: TagView | null = null;
  let D = emptyDraft();
  let cur = 0;
  let sessOK = false;
  let inFlight = false;
  let debounce: ReturnType<typeof setTimeout> | undefined;
  const today = () => dateInSiteTime(new Date());

  box.addEventListener("click", (ev) => {
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!el) return;
    if (el.dataset.act === "reload") void load();
    // The same camera as the Guest Page's Scan Again (NAV_AUDIT.md item 6). It used to be only a pop-up.
    if (el.dataset.act === "scan") openScanAgain(box, tagId);
  });

  function gate(): void {
    let ok = false;
    if (cur === 0) ok = sessOK;
    if (cur === 1) ok = !!($<HTMLInputElement>("tagFirst").value.trim() && $<HTMLInputElement>("tagLast").value.trim());
    if (cur === 2) ok = !!D.dept;
    if (cur === 3) ok = !!D.role;
    if (cur === 4) ok = !!D.years;
    if (cur === 5) {
      const c = lotoCheck(D.lotoDate, D.lotoSkipped, today());
      ok = c.kind !== "empty" && c.kind !== "future";
    }
    if (cur === 6) ok = !inFlight;
    $<HTMLButtonElement>("tagNextBtn").disabled = !ok;
  }

  function showStep(n: number): void {
    for (let i = 0; i <= LAST_STEP; i++) {
      $(`tagSt${i}`).classList.remove("active");
      $(`tagR${i}`).className = `rail-seg${i < n ? " done" : i === n ? " active" : ""}`;
    }
    $(`tagSt${n}`).classList.add("active");
    cur = n;
    $("tagBackBtn").style.display = n > 0 ? "" : "none";
    $("tagNextBtn").textContent = n === LAST_STEP ? "Claim this tag" : "Continue";
    // Each step past the first is a Back step: the phone's Back gesture is the Back button (approved 2026-10-09).
    steps().set(n, () => {
      if (cur > 0) showStep(cur - 1);
    });
    gate();
    window.scrollTo(0, 0);
  }

  function paintSession(check: SessionCheck | null, failMsg = ""): void {
    const inp = $<HTMLInputElement>("tagSess");
    const bad = $("tagSessBad");
    const good = $("tagSessGood");
    $("tagSessWork").classList.remove("on");
    inp.classList.remove("ok", "bad");
    bad.classList.remove("on");
    good.classList.remove("on");
    if (!check) {
      bad.textContent = failMsg;
      bad.classList.add("on");
      inp.classList.add("bad");
      sessOK = false;
    } else {
      const line = sessionLine(check);
      sessOK = line.ok;
      (line.ok ? good : bad).textContent = line.text;
      (line.ok ? good : bad).classList.add("on");
      inp.classList.add(line.ok ? "ok" : "bad");
    }
    gate();
  }

  async function checkCode(code: string): Promise<void> {
    try {
      const res = await callServer<SessionCheck>("checkSession", { tagId, company, sessionCode: code });
      if ($<HTMLInputElement>("tagSess").value.trim().toUpperCase() !== code) return; // typed on since
      D.sess = code;
      paintSession(res);
    } catch (e) {
      if ($<HTMLInputElement>("tagSess").value.trim().toUpperCase() !== code) return;
      paintSession(null, `Couldn't reach the GetSafeTag server. Check your connection. (${messageOf(e)})`);
    }
  }

  function sessInput(): void {
    const inp = $<HTMLInputElement>("tagSess");
    const v = inp.value.trim().toUpperCase();
    inp.value = v;
    D.sess = v;
    sessOK = false;
    inp.classList.remove("ok", "bad");
    $("tagSessBad").classList.remove("on");
    $("tagSessGood").classList.remove("on");
    $("tagSessWork").classList.remove("on");
    gate();
    if (debounce) clearTimeout(debounce);
    if (v.length < 4) return;
    $("tagSessWork").innerHTML = '<span class="sess-spinner"></span>Checking session…';
    $("tagSessWork").classList.add("on");
    debounce = setTimeout(() => void checkCode(v), 400);
  }

  async function autoFill(): Promise<void> {
    const noSession = () => {
      if ($<HTMLInputElement>("tagSess").value.trim() !== "") return;
      $("tagSessWork").textContent = "No open session detected yet. Check with your trainer, or enter the code below.";
      $("tagSessWork").classList.add("on");
    };
    try {
      const res = await callServer<{ sessionCode: string } | null>("findOpenSession", { tagId, company });
      const inp = $<HTMLInputElement>("tagSess");
      if (inp.value.trim() !== "") return;
      if (res?.sessionCode) {
        inp.value = res.sessionCode.toUpperCase();
        D.sess = inp.value;
        await checkCode(inp.value);
      } else noSession();
    } catch {
      noSession();
    }
  }

  function paintLoto(): void {
    const ok = $("tagLotoOk");
    const flag = $("tagLotoFlag");
    const err = $("tagLotoFutureErr");
    [ok, flag, err].forEach((el) => el.classList.remove("on"));
    ok.removeAttribute("style");
    $("tagLotoIcon").removeAttribute("style");
    $("tagLotoOkText").removeAttribute("style");
    $("tagLotoIcon").textContent = "✓";
    const line = lotoLine(lotoCheck(D.lotoDate, D.lotoSkipped, today()));
    if (line?.tone === "ok") {
      $("tagLotoOkText").textContent = line.text;
      ok.classList.add("on");
    } else if (line?.tone === "amber") {
      ok.style.background = "#fff3cd";
      $("tagLotoIcon").style.background = "#856404";
      $("tagLotoIcon").textContent = "!";
      $("tagLotoOkText").style.color = "#856404";
      $("tagLotoOkText").textContent = line.text;
      ok.classList.add("on");
    } else if (line?.tone === "red") {
      $("tagLotoFlagText").textContent = line.text;
      flag.classList.add("on");
    } else if (line?.tone === "bad") {
      err.textContent = line.text;
      err.classList.add("on");
    }
    gate();
  }

  function fillReview(): void {
    $("tagRvSess").textContent = D.sess || "—";
    $("tagRvName").textContent = `${D.first} ${D.last}`.trim() || "—";
    $("tagRvDept").textContent = D.dept || "—";
    $("tagRvRole").textContent = D.role || "—";
    $("tagRvYears").textContent = D.years || "—";
    const pill = $("tagRvLotoPill");
    pill.style.display = "inline-block";
    if (D.lotoSkipped) {
      $("tagRvLoto").textContent = "None";
      pill.textContent = "Not on file";
      pill.className = "review-pill n";
    } else if (D.lotoDate) {
      $("tagRvLoto").textContent = shortDate(D.lotoDate);
      pill.textContent = "Pending verification";
      pill.className = "review-pill a";
    } else {
      $("tagRvLoto").textContent = "—";
      pill.style.display = "none";
    }
  }

  async function claim(): Promise<void> {
    if (inFlight) return;
    inFlight = true;
    $("tagClaimErr").classList.remove("on");
    const next = $<HTMLButtonElement>("tagNextBtn");
    next.disabled = true;
    next.textContent = "Claiming…";
    stash(tagId, D);
    try {
      const res = await callServer<ClaimResult>("claimTag", {
        tagId,
        company,
        sessionCode: D.sess,
        firstName: D.first,
        lastName: D.last,
        department: D.dept,
        role: D.role,
        yearsExperience: D.years,
        lotoDate: D.lotoSkipped ? "" : D.lotoDate,
        lotoSkipped: D.lotoSkipped,
      });
      inFlight = false;
      if (res.kind === "claimed") {
        clearStash(tagId);
        // Claimed: Back never walks into the finished steps.
        steps().set(0, () => undefined);
        box.innerHTML = confirmationHtml(res);
        return;
      }
      if (res.kind === "alreadyClaimed") {
        steps().set(0, () => undefined);
        box.innerHTML = alreadyClaimedHtml(res.tagId, res.holderName);
        return;
      }
      showStep(0);
      sessOK = false;
      paintSession(null, res.message);
    } catch (e) {
      inFlight = false;
      $("tagClaimErr").textContent = `${messageOf(e)} Your entries are saved on this phone. Tap Claim again.`;
      $("tagClaimErr").classList.add("on");
      next.textContent = "Claim this tag";
      gate();
    }
  }

  function startEnrollment(v: Extract<TagView, { kind: "unclaimed" }>): void {
    box.innerHTML = enrollmentHtml(v.tagId, v.siteName);
    const back = recover(v.tagId);
    if (back) D = back;
    $("tagBackBtn").onclick = () => cur > 0 && showStep(cur - 1);
    $("tagNextBtn").onclick = () => {
      if (inFlight) return;
      if (cur === 1) {
        D.first = $<HTMLInputElement>("tagFirst").value.trim();
        D.last = $<HTMLInputElement>("tagLast").value.trim();
      }
      if (cur === 5) fillReview();
      if (cur === LAST_STEP) {
        void claim();
        return;
      }
      showStep(cur + 1);
    };
    $("tagSess").oninput = sessInput;
    $("tagFirst").oninput = gate;
    $("tagLast").oninput = gate;
    $("tagLoto").onchange = () => {
      D.lotoDate = $<HTMLInputElement>("tagLoto").value;
      paintLoto();
    };
    $("tagLotoSkip").onchange = () => {
      const skip = $<HTMLInputElement>("tagLotoSkip").checked;
      D.lotoSkipped = skip;
      const input = $<HTMLInputElement>("tagLoto");
      input.disabled = skip;
      if (skip) {
        input.value = "";
        D.lotoDate = "";
      }
      paintLoto();
    };
    box.querySelectorAll<HTMLButtonElement>("#tagStepwrap .choice").forEach((btn) => {
      const field = btn.dataset.field as "dept" | "role" | "years";
      if (D[field] === btn.dataset.val) btn.classList.add("sel");
      btn.onclick = () => {
        btn.parentElement!.querySelectorAll(".choice").forEach((b) => b.classList.remove("sel"));
        btn.classList.add("sel");
        D[field] = btn.dataset.val ?? "";
        gate();
      };
    });
    box.querySelectorAll<HTMLButtonElement>("#tagStepwrap .review-edit").forEach((b) => {
      b.onclick = () => showStep(Number(b.dataset.step));
    });
    if (back) {
      $<HTMLInputElement>("tagFirst").value = back.first;
      $<HTMLInputElement>("tagLast").value = back.last;
      if (back.lotoSkipped) {
        $<HTMLInputElement>("tagLotoSkip").checked = true;
        $<HTMLInputElement>("tagLoto").disabled = true;
      } else if (back.lotoDate) $<HTMLInputElement>("tagLoto").value = back.lotoDate;
      paintLoto();
    }
    showStep(0);
    void autoFill();
  }

  async function load(): Promise<void> {
    box.innerHTML = loadingHtml();
    try {
      view = await callServer<TagView>("loadTag", { tagId, company });
    } catch (e) {
      box.innerHTML = errorHtml(`Couldn't reach the GetSafeTag server. Check your connection and try again. (${messageOf(e)})`);
      return;
    }
    if (view.kind === "unclaimed") startEnrollment(view);
    else if (view.kind === "claimed") {
      if (onClaimed) {
        document.body.classList.remove("tagpage");
        onClaimed(view);
      } else box.innerHTML = claimedHtml(view);
    }
    else if (view.kind === "disabled")
      box.innerHTML = statusHtml(
        "This tag is out of service",
        "See your ESP Manager",
        "red",
        "No further action here",
        "This tag has been retired from the program. If you were expecting to enroll or check status, see your ESP Manager for a current tag.",
        stripHtml(view.tagId, view.siteName, "Out of service", "oos"),
      );
    else
      box.innerHTML = statusHtml(
        "Tag not recognized",
        "Not registered in GetSafeTag ESP",
        "red",
        esc(view.tagId),
        "This tag is not in the GetSafeTag registry. If you scanned a physical bag tag, confirm the ID on the tag matches the QR code, and check with your ESP Manager to see whether this tag should be added.",
      );
  }

  void load();
}
